import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useConfiguration } from '../../context/configurationContext';

import css from './ListingPage.module.css';

/**
 * ProductBreadcrumb component displays category navigation breadcrumb
 * Only visible on desktop screens
 * 
 * @param {Object} publicData - Listing public data containing categoryLevel1, categoryLevel2, and categoryLevel3
 * @param {string} productTitle - The title of the current product
 */
const ProductBreadcrumb = ({ publicData, productTitle }) => {
  const config = useConfiguration();
  const history = useHistory();
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!publicData) {
    return null;
  }

  const { categoryLevel1, categoryLevel2, categoryLevel3 } = publicData;

  // Don't render if no category is set
  if (!categoryLevel1) {
    return null;
  }

  // Get categories from configuration
  const categories = config?.categoryConfiguration?.categories || [];

  /**
   * Get display name for a category by its ID
   * @param {string} categoryId - The category ID
   * @returns {string} - The display name or the ID if not found
   */
  const getCategoryDisplayName = (categoryId) => {
    try {
      const found = categories.find(c => c.id === categoryId);
      if (found && found.name) return found.name;
      // Fallback: title-case the technical ID
      if (typeof categoryId === 'string') {
        return categoryId
          .replace(/-/g, ' ')
          .replace(/\b\w/g, ch => ch.toUpperCase());
      }
      return categoryId;
    } catch (_) {
      return categoryId;
    }
  };

  /**
   * Get display name for a subcategory by its ID
   * @param {string} categoryId - The parent category ID
   * @param {string} subcategoryId - The subcategory ID
   * @returns {string} - The display name or the ID if not found
   */
  const getSubcategoryDisplayName = (categoryId, subcategoryId) => {
    try {
      const category = categories.find(c => c.id === categoryId);
      if (category && category.subcategories) {
        const found = category.subcategories.find(sc => sc.id === subcategoryId);
        if (found && found.name) return found.name;
      }
      // Fallback: title-case the technical ID
      if (typeof subcategoryId === 'string') {
        return subcategoryId
          .replace(/-/g, ' ')
          .replace(/\b\w/g, ch => ch.toUpperCase());
      }
      return subcategoryId;
    } catch (_) {
      return subcategoryId;
    }
  };

  /**
   * Get display name for a sub-subcategory (level 3) by its ID
   * @param {string} categoryId - The parent category ID
   * @param {string} subcategoryId - The subcategory ID (level 2)
   * @param {string} subSubcategoryId - The sub-subcategory ID (level 3)
   * @returns {string} - The display name or the ID if not found
   */
  const getSubSubcategoryDisplayName = (categoryId, subcategoryId, subSubcategoryId) => {
    try {
      const category = categories.find(c => c.id === categoryId);
      if (category && category.subcategories) {
        const subcategory = category.subcategories.find(sc => sc.id === subcategoryId);
        if (subcategory && subcategory.subcategories) {
          const found = subcategory.subcategories.find(ssc => ssc.id === subSubcategoryId);
          if (found && found.name) return found.name;
        }
      }
      // Fallback: title-case the technical ID
      if (typeof subSubcategoryId === 'string') {
        return subSubcategoryId
          .replace(/-/g, ' ')
          .replace(/\b\w/g, ch => ch.toUpperCase());
      }
      return subSubcategoryId;
    } catch (_) {
      return subSubcategoryId;
    }
  };

  // Get display labels
  const categoryLabel = getCategoryDisplayName(categoryLevel1);
  const subcategoryLabel = categoryLevel2 
    ? getSubcategoryDisplayName(categoryLevel1, categoryLevel2) 
    : null;
  const subSubcategoryLabel = categoryLevel3
    ? getSubSubcategoryDisplayName(categoryLevel1, categoryLevel2, categoryLevel3)
    : null;

  const crumbs = [
    {
      key: 'all-products',
      label: 'Tous les produits',
      onClick: () => history.push('/products'),
    },
    {
      key: 'category',
      label: categoryLabel,
      onClick: () => history.push(`/products?category=${categoryLevel1}`),
    },
  ];

  if (subcategoryLabel) {
    crumbs.push({
      key: 'subcategory',
      label: subcategoryLabel,
      onClick: () =>
        history.push(`/products?category=${categoryLevel1}&subcategory=${categoryLevel2}`),
    });
  }

  if (subSubcategoryLabel) {
    crumbs.push({
      key: 'subsubcategory',
      label: subSubcategoryLabel,
      onClick: () =>
        history.push(
          `/products?category=${categoryLevel1}&subcategory=${categoryLevel2}&subsubcategory=${categoryLevel3}`
        ),
    });
  }

  if (productTitle) {
    crumbs.push({ key: 'product', label: productTitle });
  }

  const showCollapse = crumbs.length > 3;
  const listClassName = showCollapse
    ? isExpanded
      ? `${css.breadcrumbList} ${css.breadcrumbListExpanded}`
      : `${css.breadcrumbList} ${css.breadcrumbListCollapsed}`
    : css.breadcrumbList;

  return (
    <nav className={css.breadcrumb} aria-label="Breadcrumb">
      <ol className={listClassName}>
        {crumbs.map((crumb, index) => {
          const isCurrent = index === crumbs.length - 1;
          const isMiddle = index > 0 && index < crumbs.length - 1;
          const prevIsMiddle = index - 1 > 0 && index - 1 < crumbs.length - 1;
          const isSeparatorMiddle = index > 0 && (isMiddle || prevIsMiddle);
          return (
            <React.Fragment key={crumb.key}>
              {index > 0 ? (
                <li
                  className={`${css.breadcrumbSeparator}${
                    isSeparatorMiddle ? ` ${css.breadcrumbSeparatorMiddle}` : ''
                  }`}
                  aria-hidden="true"
                >
                  /
                </li>
              ) : null}
              <li
                className={`${css.breadcrumbItem}${
                  isMiddle ? ` ${css.breadcrumbItemMiddle}` : ''
                }`}
              >
                {!isCurrent && crumb.onClick ? (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      crumb.onClick();
                    }}
                    className={css.breadcrumbLink}
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span className={css.breadcrumbLinkCurrent} aria-current="page">
                    {crumb.label}
                  </span>
                )}
              </li>
              {index === 0 && showCollapse ? (
                <>
                  <li
                    className={`${css.breadcrumbSeparator} ${css.breadcrumbSeparatorCollapsed}`}
                    aria-hidden="true"
                  >
                    /
                  </li>
                  <li className={`${css.breadcrumbItem} ${css.breadcrumbItemCollapsed}`}>
                    <button
                      type="button"
                      className={css.breadcrumbEllipsisButton}
                      onClick={() => setIsExpanded(true)}
                      aria-label="Afficher le fil d'ariane complet"
                    >
                      ...
                    </button>
                  </li>
                  <li
                    className={`${css.breadcrumbSeparator} ${css.breadcrumbSeparatorCollapsed}`}
                    aria-hidden="true"
                  >
                    /
                  </li>
                </>
              ) : null}
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

export default ProductBreadcrumb;

